/**
 * teacherPreset
 *
 * Derives a "preset" — subject bucket, year, state, level, taskType — from
 * the teacher's actual classes + profile. The onboarding tour uses this
 * preset to:
 *   - pre-fill the year/subject/state dropdowns in the new-task mirror
 *   - pick the best-matching rubric/SC/SA from the teacher's preloaded
 *     real templates (see pickTemplate.js)
 *   - select a subject-specific demo draft + feedback annotations
 *     (see draftSubjectExemplars.js)
 *
 * Everything degrades to English/Year 12/NSW when class metadata is
 * missing (e.g. fake-mode users on local dev whose classes don't have
 * subject/year fields). The fallback exemplar covers that case.
 */

export const SUBJECT_BUCKETS = {
  english: 'english',
  modernHistory: 'modernHistory',
  ancientHistory: 'ancientHistory',
  history: 'history',
  geography: 'geography',
  business: 'business',
  economics: 'economics',
  legal: 'legal',
  drama: 'drama',
  healthPe: 'healthPe',
};

// Order matters — more specific patterns first so e.g. "Modern History"
// doesn't get caught by the generic "history" matcher.
const SUBJECT_MATCHERS = [
  { bucket: 'modernHistory', test: (s) => /\bmodern[\s_-]?history\b|revolutions?/i.test(s) },
  { bucket: 'ancientHistory', test: (s) => /\bancient[\s_-]?history\b/i.test(s) },
  { bucket: 'business', test: (s) => /\bbusiness\b|\bcommerce\b/i.test(s) },
  { bucket: 'economics', test: (s) => /\beconomics?\b/i.test(s) },
  { bucket: 'legal', test: (s) => /\blegal\b/i.test(s) },
  { bucket: 'geography', test: (s) => /\bgeography\b/i.test(s) },
  { bucket: 'history', test: (s) => /\bhistory\b/i.test(s) },
  { bucket: 'drama', test: (s) => /\bdrama\b|\btheatre\b|\bperforming arts?\b/i.test(s) },
  {
    bucket: 'healthPe',
    test: (s) =>
      /\bphysical\s+education\b|\bpdhpe\b|\bhealth\s+and\s+(human\s+development|movement)\b/i.test(
        s
      ),
  },
  { bucket: 'english', test: (s) => /\benglish\b|\bliterature\b|\beal\b/i.test(s) },
];

function bucketForSubjectName(subjectName) {
  if (!subjectName || typeof subjectName !== 'string') return null;
  const hit = SUBJECT_MATCHERS.find((m) => m.test(subjectName));
  return hit ? hit.bucket : null;
}

function tallyMode(values, fallback) {
  const counts = new Map();
  for (const v of values) {
    if (v === null || v === undefined || v === '') continue;
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  if (counts.size === 0) return fallback;
  // Highest count wins; ties broken by first appearance (Map preserves insertion order).
  let best = fallback;
  let bestN = 0;
  for (const [k, n] of counts.entries()) {
    if (n > bestN) {
      bestN = n;
      best = k;
    }
  }
  return best;
}

function collectSubjects(classes) {
  const out = [];
  for (const cls of classes || []) {
    const subjects = cls?.subjects || [];
    if (subjects.length > 0) {
      for (const s of subjects) {
        if (s && typeof s === 'object') out.push(s);
        else if (typeof s === 'string') out.push({ subjectName: s });
      }
      continue;
    }
    // Fall back to parsing the class title. Production backends populate
    // subjects[], but fake-mode + some onboarding flows give us classes
    // with empty subjects and titles like "Year 11 Modern History" or
    // "Year 11 Business Studies". Strip the leading year and any common
    // grade word so the matchers in bucketForSubjectName can recognise
    // the subject.
    const title = (cls?.title || '').trim();
    if (!title) continue;
    const stripped = title
      .replace(/^year\s*\d+\s*[-:]?\s*/i, '')
      .replace(/^grade\s*\d+\s*[-:]?\s*/i, '')
      .replace(/^class\s*[-:]?\s*/i, '')
      .trim();
    if (!stripped || stripped.toLowerCase() === title.toLowerCase()) {
      // No leading year stripped — only push if the title itself looks
      // subject-y (i.e. matches one of our buckets directly).
      if (bucketForSubjectName(title)) out.push({ subjectName: title });
      continue;
    }
    out.push({ subjectName: stripped });
  }
  return out;
}

function pickYear(classes, profile) {
  // Take every year mentioned on every subject and pick the most common.
  // Classes win because they are the richer signal, but a student or an
  // individual teacher HAS no classes — for them the only thing we know is
  // the year they picked in onboarding (StateYearDialogue PATCHes it onto
  // the profile as a bare '12' / '9' before the demo ever renders). Reading
  // it here is the difference between the demo saying "Year 12" at everyone
  // and saying what the user just told us. '12' stays the last resort.
  const years = [];
  for (const cls of classes || []) {
    for (const s of cls?.subjects || []) {
      const list = s?.years || s?.year ? (Array.isArray(s?.years) ? s.years : [s.year]) : [];
      for (const y of list) if (y) years.push(String(y));
    }
    if (cls?.year) years.push(String(cls.year));
    // Fallback: parse "Year N" / "Grade N" from class title. Same reason as
    // collectSubjects — production has the metadata field populated, but
    // some flows give us only the title.
    const title = String(cls?.title || '');
    const m = title.match(/\b(?:year|grade)\s*(\d{1,2})\b/i);
    if (m) years.push(m[1]);
  }
  // The profile year is not reliably a bare number: c0c4a617e had to add
  // the same normalisation on the success-criteria save path, which is the
  // evidence that "Year 7" and "Grade 7" reach us too. Strip the prefix
  // rather than failing the digit test and silently falling back to 12.
  const fromProfile = String(profile?.year ?? '')
    .trim()
    .replace(/^(?:year|grade)\s*-?\s*(\d+)$/i, '$1');
  return tallyMode(years, /^\d{1,2}$/.test(fromProfile) ? fromProfile : '12');
}

/**
 * ISO-2 country for the profile, uppercased, or null when we genuinely
 * don't know. Null is NOT folded into 'AU': roughly half of production
 * profiles carry no country, and `samplesForRegion` in components-mobile
 * already established the house rule that an unplaceable user gets the
 * region-neutral content rather than Australia's. Region-specific
 * exemplars are opt-in on a known country, never on a guess.
 */
function pickCountry(profile) {
  const raw = profile?.country;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().toUpperCase();
  return trimmed || null;
}

function pickState(classes, profile) {
  if (Array.isArray(profile?.states) && profile.states[0]) return profile.states[0];
  if (typeof profile?.state === 'string' && profile.state) return profile.state;
  const fromClasses = [];
  for (const cls of classes || []) {
    for (const s of cls?.subjects || []) {
      const list = Array.isArray(s?.states) ? s.states : s?.state ? [s.state] : [];
      for (const st of list) if (st) fromClasses.push(st);
    }
  }
  return tallyMode(fromClasses, 'NSW');
}

function pickBucket(subjects) {
  const buckets = subjects.map((s) => bucketForSubjectName(s?.subjectName)).filter(Boolean);
  // Anything we couldn't bucket counts as "english" — that's our default and
  // the subject with by far the most template coverage in the seed data.
  return tallyMode(buckets, 'english');
}

function pickSubjectName(subjects, bucket) {
  // Prefer a real subject name from the teacher's classes that maps to the
  // chosen bucket; fall back to a sensible human label per bucket.
  const match = subjects.find((s) => bucketForSubjectName(s?.subjectName) === bucket);
  if (match?.subjectName) return match.subjectName;
  return SUBJECT_LABEL[bucket] || 'English';
}

function pickSubjectCode(subjects, bucket) {
  const match = subjects.find((s) => bucketForSubjectName(s?.subjectName) === bucket);
  return match?.subjectCode || null;
}

const SUBJECT_LABEL = {
  english: 'English',
  modernHistory: 'Modern History',
  ancientHistory: 'Ancient History',
  history: 'History',
  geography: 'Geography',
  business: 'Business Studies',
  economics: 'Economics',
  legal: 'Legal Studies',
  drama: 'Drama',
  healthPe: 'Physical Education',
};

// Per-bucket task type that reads naturally on the demo assignment card.
// These are the labels JeddAI uses across rubrics — keeping them aligned
// helps the demo feel like the real product.
const TASK_TYPE_BY_BUCKET = {
  english: 'Analytical Essay',
  modernHistory: 'Source Analysis',
  ancientHistory: 'Source Analysis',
  history: 'Source Analysis',
  geography: 'Geographical Inquiry',
  business: 'Business Report',
  economics: 'Extended Response',
  legal: 'Extended Response',
  drama: 'Performance Analysis',
  healthPe: 'Extended Response',
};

/**
 * Build the onboarding preset for a teacher.
 * @param {Array} classes  output of useActiveClassData() — array of class objects with optional .subjects[]
 * @param {Object} profile output of useProfile() — for state, year and country
 * @returns {{bucket, subject, subjectCode, state, country, year, level, taskType}}
 */
export function deriveTeacherPreset(classes, profile) {
  const subjects = collectSubjects(classes);
  const bucket = pickBucket(subjects);
  const year = pickYear(classes, profile);
  const state = pickState(classes, profile);
  // Year 13 is senior in New Zealand. It was harmless while every
  // class-less user resolved to '12' regardless of what they'd chosen;
  // now that the profile year actually reaches here, omitting 13 would
  // hand every NZ Year 13 student the junior exemplar.
  const isSenior = /^(11|12|13)$/.test(String(year))
    || (classes || []).some((cls) =>
      (cls?.subjects || []).some((s) =>
        (Array.isArray(s?.years) ? s.years : [s?.year]).some((y) => /^(11|12|13)$/.test(String(y)))
      )
    );
  return {
    bucket,
    subject: pickSubjectName(subjects, bucket),
    subjectCode: pickSubjectCode(subjects, bucket),
    state,
    country: pickCountry(profile),
    year,
    level: isSenior ? 'senior' : 'junior',
    taskType: TASK_TYPE_BY_BUCKET[bucket] || 'Extended Response',
  };
}

// Exposed for tests and for the bucketed exemplar lookup.
export { bucketForSubjectName, SUBJECT_LABEL };
